import { type Cesium3DTileset, type ClippingPlane, ClippingPlaneCollection } from 'cesium';

// When Cesium renders translucent depth for a pick (on while a splat or the Google
// underground view is shown), it runs the last frame's draw commands again without
// rebuilding them. A clipping collection destroyed since that frame leaves them sampling
// a texture that is gone, and rendering stops with "Cannot read properties of undefined
// (reading '_target')". Assigning anything to `tileset.clippingPlanes` destroys the
// collection it replaces, so the box keeps one collection per tileset and only switches
// it on and off; Cesium destroys it with the tileset.
const boxCollections = new WeakMap<Cesium3DTileset, ClippingPlaneCollection>();

/** Clips the tileset with the box's collection, reusing the one it already has. */
export const clipWithBox = (tileset: Cesium3DTileset, createPlanes: () => ClippingPlane[]): ClippingPlaneCollection => {
	const current = boxCollections.get(tileset);
	if (current && !current.isDestroyed() && tileset.clippingPlanes === current) {
		current.enabled = true;
		return current;
	}

	const collection = new ClippingPlaneCollection({
		planes: createPlanes(),
		edgeWidth: 0.0,
		enabled: true,
		unionClippingRegions: true,
	});
	tileset.clippingPlanes = collection;
	boxCollections.set(tileset, collection);
	return collection;
};

/** Stops the box clipping the tileset without destroying anything a queued draw command may sample. */
export const pauseBoxClipping = (tileset: Cesium3DTileset) => {
	const collection = boxCollections.get(tileset);
	if (collection && !collection.isDestroyed() && tileset.clippingPlanes === collection) {
		collection.enabled = false;
	}
};
